import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const GOOGLE_CLIENT_ID = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID || '';
const APPLE_CLIENT_ID = (import.meta as any).env?.VITE_APPLE_CLIENT_ID || '';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: { client_id: string; callback: (res: { credential: string }) => void }) => void;
          renderButton: (el: HTMLElement, options: Record<string, unknown>) => void;
        };
      };
    };
    __NOOX_GOOGLE_GSI_INITIALIZED__?: boolean;
    AppleID?: {
      auth: {
        init: (config: { clientId: string; scope: string; redirectURI: string; usePopup: boolean }) => void;
        signIn: () => Promise<{ authorization: { id_token: string } }>;
      };
    };
  }
}

type SocialSignInButtonsProps = {
  onGoogleToken: (idToken: string) => void;
  onAppleToken: (idToken: string) => void;
  disabled?: boolean;
  consentChecked?: boolean;
  onConsentRequired?: () => void;
};

export const SocialSignInButtons: React.FC<SocialSignInButtonsProps> = ({
  onGoogleToken,
  onAppleToken,
  disabled,
  consentChecked,
  onConsentRequired
}) => {
  const { t } = useTranslation();
  const googleButtonRef = useRef<HTMLDivElement>(null);
  const googleCallbackRef = useRef(onGoogleToken);
  const [googleReady, setGoogleReady] = useState(false);
  const [googleTimedOut, setGoogleTimedOut] = useState(false);
  const [appleReady, setAppleReady] = useState(false);
  const [isCompactMobile, setIsCompactMobile] = useState(() => (
    typeof window !== 'undefined' ? window.innerWidth <= 640 : false
  ));

  useEffect(() => {
    googleCallbackRef.current = onGoogleToken;
  }, [onGoogleToken]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleResize = () => setIsCompactMobile(window.innerWidth <= 640);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !consentChecked || googleButtonRef.current === null) return;

    const renderGoogleButton = () => {
      if (!googleButtonRef.current || !window.google?.accounts?.id?.renderButton) return;
      googleButtonRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        type: 'standard',
        theme: 'outline',
        size: isCompactMobile ? 'medium' : 'large',
        width: isCompactMobile ? 280 : 320,
        text: 'continue_with'
      });
      setGoogleReady(true);
    };

    const initializeGoogle = () => {
      if (!window.google?.accounts?.id) return;
      if (!window.__NOOX_GOOGLE_GSI_INITIALIZED__) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (res) => googleCallbackRef.current(res.credential)
        });
        window.__NOOX_GOOGLE_GSI_INITIALIZED__ = true;
      }
      renderGoogleButton();
    };

    if (typeof window.google?.accounts?.id?.renderButton === 'function') {
      initializeGoogle();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://accounts.google.com/gsi/client"]');
    if (existingScript) {
      existingScript.addEventListener('load', initializeGoogle, { once: true });
      return () => existingScript.removeEventListener('load', initializeGoogle);
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.addEventListener('load', initializeGoogle, { once: true });
    document.head.appendChild(script);
    return () => script.removeEventListener('load', initializeGoogle);
  }, [consentChecked, isCompactMobile]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !consentChecked || googleReady || googleTimedOut) return;
    const timeoutId = window.setTimeout(() => setGoogleTimedOut(true), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [consentChecked, googleReady, googleTimedOut]);

  useEffect(() => {
    if (!APPLE_CLIENT_ID || !consentChecked) return;
    if (typeof window.AppleID?.auth?.init === 'function') {
      setAppleReady(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
    script.async = true;
    script.onload = () => setAppleReady(true);
    document.head.appendChild(script);
    return () => script.remove();
  }, [consentChecked]);

  const handleAppleClick = async () => {
    if (!consentChecked && onConsentRequired) {
      onConsentRequired();
      return;
    }
    if (disabled || !APPLE_CLIENT_ID || !window.AppleID?.auth) return;
    try {
      window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: 'name email',
        redirectURI: typeof window !== 'undefined' ? window.location.origin : '',
        usePopup: true
      });
      const res = await window.AppleID.auth.signIn();
      const idToken = res?.authorization?.id_token;
      if (idToken) onAppleToken(idToken);
    } catch (err) {
      console.error('Apple sign-in error', err);
    }
  };

  const showGoogle = Boolean(GOOGLE_CLIENT_ID);
  const showApple = Boolean(APPLE_CLIENT_ID);
  const showSocialCaption = !isCompactMobile;
  const googleStatusCopy = !consentChecked
    ? t('auth.acceptTerms')
    : !googleReady
      ? t('common.loading')
      : null;

  if ((!showGoogle || googleTimedOut) && !showApple) return null;

  return (
    <div className={`sessionSocialButtons${isCompactMobile ? ' sessionSocialButtons--compact' : ''}`}>
      {showApple && (
        <div className="sessionSocialRow">
          <button
            type="button"
            className="sessionSocialBtn sessionSocialBtnApple"
            disabled={disabled || !appleReady}
            onClick={handleAppleClick}
          >
            <span className="sessionSocialBtnIcon" aria-hidden="true">
              <svg viewBox="0 0 24 24" focusable="false">
                <path d="M15.37 2.64c0 1.14-.42 2.19-1.1 2.97-.82.95-2.16 1.69-3.44 1.59-.16-1.1.38-2.28 1.04-3.04.73-.86 2-1.53 3.18-1.52.2 0 .29.01.32.02 0 .03 0 .1 0 .18Zm3.13 14.42c-.45 1.01-.99 1.93-1.71 2.88-.99 1.29-1.8 2.9-3.37 2.92-1.4.03-1.85-.83-3.45-.82-1.6.01-2.09.84-3.48.8-1.56-.03-2.33-1.5-3.32-2.79C.38 17.33-.53 12.3 1.56 8.94c1-1.6 2.79-2.61 4.48-2.61 1.39 0 2.7.96 3.45.96.74 0 2.38-1.18 4.02-1.01.69.03 2.63.28 3.88 2.11-.1.06-2.32 1.35-2.29 4 .04 3.16 2.78 4.21 2.8 4.22-.02.08-.43 1.47-1.4 3.12Z" fill="currentColor" />
              </svg>
            </span>
            <span className="sessionSocialBtnCopy">
              <span>{t('auth.continueWithApple')}</span>
              {showSocialCaption ? <small>{t('auth.socialPrivateTrack')}</small> : null}
            </span>
            <span className="sessionSocialBtnArrow" aria-hidden="true">-&gt;</span>
          </button>
        </div>
      )}

      {showGoogle && !googleTimedOut && (
        <div className="sessionSocialRow">
          <div className="sessionSocialGoogleWrap">
            {!consentChecked && (
              <button
                type="button"
                className="sessionSocialBlocker"
                aria-label={t('auth.acceptTerms')}
                onClick={onConsentRequired}
              />
            )}
            <div className="sessionSocialMeta">
              <span className="sessionSocialLabel">{t('auth.continueWithGoogle')}</span>
              {showSocialCaption ? <span className="sessionSocialCaption">{t('auth.socialFastTrack')}</span> : null}
            </div>
            {googleStatusCopy && <span className="sessionSocialPlaceholder">{googleStatusCopy}</span>}
            <div ref={googleButtonRef} className="sessionSocialGoogleInner" />
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialSignInButtons;
